// src/components/ErrorState.js

import React from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    useTheme,
    Avatar,
    Zoom,
    Fade,
    Chip
} from '@mui/material';
import {
    PointOfSale,
    Refresh,
    Warning,
    BugReport,
    CloudOff,
    ReportProblem,
    ShoppingCart,
    Storefront,
    ReceiptLong,
    Inventory,
    Payments,
    BusinessCenter,
    Error as ErrorIcon,
    Lock,
    Block,
    GppBad,
    LinkOff
} from '@mui/icons-material';

const ErrorState = ({
    error,
    onRetry,
    title = "Oops! Something went wrong",
    icon = null,
    variant = "default",
    fullPage = false,
    showRetry = true,
    retryText = "Try Again",
    secondaryAction = null,
    secondaryText = null
}) => {
    const theme = useTheme();

    // POS-specific icons for different variants
    const getIcon = () => {
        if (icon) return icon;

        switch (variant) {
            case 'pos':
            case 'default':
                return <PointOfSale sx={{ fontSize: 64 }} />;
            case 'connection':
                return <CloudOff sx={{ fontSize: 64 }} />;
            case 'network':
                return <LinkOff sx={{ fontSize: 64 }} />;
            case 'permission':
                return <Lock sx={{ fontSize: 64 }} />;
            case 'cart':
                return <ShoppingCart sx={{ fontSize: 64 }} />;
            case 'inventory':
                return <Inventory sx={{ fontSize: 64 }} />;
            case 'payment':
                return <Payments sx={{ fontSize: 64 }} />;
            case 'receipt':
                return <ReceiptLong sx={{ fontSize: 64 }} />;
            case 'store':
                return <Storefront sx={{ fontSize: 64 }} />;
            case 'business':
                return <BusinessCenter sx={{ fontSize: 64 }} />;
            case 'warning':
                return <Warning sx={{ fontSize: 64 }} />;
            case 'bug':
                return <BugReport sx={{ fontSize: 64 }} />;
            case 'blocked':
                return <Block sx={{ fontSize: 64 }} />;
            case 'security':
                return <GppBad sx={{ fontSize: 64 }} />;
            default:
                return <ErrorIcon sx={{ fontSize: 64 }} />;
        }
    };

    // Get color based on variant
    const getColor = () => {
        switch (variant) {
            case 'connection':
            case 'network':
                return theme.palette.warning.main;
            case 'permission':
            case 'security':
            case 'blocked':
                return theme.palette.error.dark;
            case 'cart':
            case 'store':
            case 'retail':
                return theme.palette.secondary.main;
            case 'inventory':
                return theme.palette.info.main;
            case 'payment':
            case 'receipt':
                return theme.palette.success.main;
            case 'bug':
                return theme.palette.info.dark;
            default:
                return theme.palette.error.main;
        }
    };

    // Get gradient based on variant
    const getGradient = () => {
        const color = getColor();
        return `linear-gradient(135deg, ${color}, ${color}dd)`;
    };

    const mainColor = getColor();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: fullPage ? '100vh' : '400px',
                width: '100%',
                gap: 2,
                p: 4,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '15%',
                    width: '250px',
                    height: '250px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${mainColor}10 0%, transparent 70%)`,
                    filter: 'blur(50px)',
                    zIndex: 0
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    left: '10%',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${mainColor}08 0%, transparent 70%)`,
                    filter: 'blur(40px)',
                    zIndex: 0
                }}
            />

            <Zoom in={true}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Hexagonal background */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 140,
                            height: 140,
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            background: `${mainColor}15`,
                            animation: 'rotate-hex 20s linear infinite',
                            '@keyframes rotate-hex': {
                                '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                                '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
                            },
                            zIndex: -1
                        }}
                    />

                    {/* Error icon with shake animation */}
                    <Box
                        sx={{
                            animation: 'shake 0.6s ease-in-out',
                            '@keyframes shake': {
                                '0%, 100%': { transform: 'translateX(0)' },
                                '10%, 50%, 90%': { transform: 'translateX(-6px)' },
                                '30%, 70%': { transform: 'translateX(6px)' },
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 110,
                                height: 110,
                                background: getGradient(),
                                color: 'white',
                                boxShadow: `0 12px 40px ${mainColor}50`,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {getIcon()}
                        </Avatar>
                    </Box>

                    {/* Error badge */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -5,
                            right: -5,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 18,
                            fontWeight: 'bold',
                            boxShadow: `0 4px 12px ${theme.palette.error.main}50`,
                            animation: 'pulse-badge 2s ease-in-out infinite',
                            '@keyframes pulse-badge': {
                                '0%': { 
                                    transform: 'scale(1)',
                                    boxShadow: `0 0 0 0 ${theme.palette.error.main}60`
                                },
                                '70%': { 
                                    transform: 'scale(1.1)',
                                    boxShadow: `0 0 0 12px ${theme.palette.error.main}00`
                                },
                                '100%': { 
                                    transform: 'scale(1)',
                                    boxShadow: `0 0 0 0 ${theme.palette.error.main}00`
                                },
                            },
                        }}
                    >
                        !
                    </Box>
                </Box>
            </Zoom>

            <Fade in={true} timeout={800}>
                <Box sx={{ textAlign: 'center', maxWidth: 550, zIndex: 1 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 700,
                            mb: 1,
                            letterSpacing: '-0.5px'
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            mb: 3,
                            maxWidth: 400,
                            mx: 'auto'
                        }}
                    >
                        Something didn't work as expected. Don't worry, these things happen!
                    </Typography>

                    {/* Error message card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: `${mainColor}08`,
                            border: `1.5px solid ${mainColor}25`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                background: getGradient(),
                                borderRadius: '2px 0 0 2px'
                            }
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: 1.5,
                            textAlign: 'left'
                        }}>
                            <ErrorIcon sx={{ 
                                color: mainColor, 
                                mt: 0.3,
                                flexShrink: 0
                            }} />
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: mainColor,
                                        fontWeight: 600,
                                        mb: 0.5
                                    }}
                                >
                                    Error Details
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        wordBreak: 'break-word',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {error || "An unexpected error occurred. Please try again or contact support."}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Action buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {showRetry && onRetry && (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Refresh />}
                                onClick={onRetry}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    background: getGradient(),
                                    boxShadow: `0 8px 20px ${mainColor}40`,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 12px 28px ${mainColor}60`,
                                        background: getGradient(),
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {retryText}
                            </Button>
                        )}

                        {secondaryAction && (
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={secondaryAction}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    borderColor: mainColor,
                                    color: mainColor,
                                    '&:hover': {
                                        borderColor: mainColor,
                                        background: `${mainColor}10`,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {secondaryText || 'Go Back'}
                            </Button>
                        )}
                    </Box>

                    {/* Help text */}
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.disabled,
                            display: 'block',
                            mt: 3
                        }}
                    >
                        If the problem persists, please contact system support
                    </Typography>
                </Box>
            </Fade>

            {/* Corner decorative icon */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    opacity: 0.03,
                    zIndex: 0
                }}
            >
                <PointOfSale sx={{ fontSize: 180 }} />
            </Box>
        </Box>
    );
};

export default ErrorState;